import { CSSProperties, FunctionComponent, ReactNode, Ref } from 'react';
import { Group, GroupImperativeHandle, Layout } from 'react-resizable-panels';

/** Map of panel id to its share of the group (0..100). */
export type PanelLayout = Layout;
export type PanelGroupHandle = GroupImperativeHandle;

export interface IPanelGroupProps {
  direction?: 'horizontal' | 'vertical';
  id?: string;
  style?: CSSProperties;
  className?: string;
  disabled?: boolean;
  /** Initial layout; applied only when it has a value for every mounted panel. */
  defaultLayout?: PanelLayout;
  /** Called after a layout change settles (at mount, on pointer release, after setLayout, when the panel set changes). */
  onLayoutChanged?: (layout: PanelLayout) => void;
  groupRef?: Ref<PanelGroupHandle | null>;
  // v4 detects resizes by the Separator's bounding box, but our handle is a 1px element (so it adds
  // no layout gap), which makes it nearly impossible to grab. Widen the hit region here to match the
  // handle's 12px grab area (RESIZE_HANDLE_HIT_AREA) — the v4 equivalent of the v3 `::before` hit area.
  resizeTargetMinimumSize?: { coarse: number; fine: number };
  // v4 injects a global `cursor: ... !important` stylesheet that flips between col-resize (hover)
  // and ew-resize (active) and overrides the handle's own cursor. Disable it so the cursor comes
  // solely from the ResizeHandle element — consistent, and matching the custom navigation drawer.
  disableCursor?: boolean;
  children?: ReactNode;
}

// Matches RESIZE_HANDLE_HIT_AREA (12px) in @cbridge-tools/resize-handle; coarse pointers get a
// larger target. Kept inline to avoid a circular dependency with the resize-handle package.
const HANDLE_HIT_AREA = { coarse: 24, fine: 12 };

// Compatibility wrapper over react-resizable-panels v4 `Group`. Keeps the v3 `direction` prop name,
// a grabbable resize hit area, and a single handle-owned cursor so call sites stay unchanged.
export const PanelGroup: FunctionComponent<IPanelGroupProps> = ({
  direction = 'horizontal',
  resizeTargetMinimumSize = HANDLE_HIT_AREA,
  disableCursor = true,
  children,
  ...rest
}) => (
  <Group
    orientation={direction}
    resizeTargetMinimumSize={resizeTargetMinimumSize}
    disableCursor={disableCursor}
    {...rest}
  >
    {children}
  </Group>
);
